import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Phone, Calendar, Plane, Clock, Shield, 
  CreditCard, Users, CheckCircle2
} from 'lucide-react';

const AirportPage = () => {
  const { language, getRoute } = useLanguage();

  // Content based on language
  const content = {
    fr: {
      title: "Taxi Aéroport Montréal-Trudeau (YUL) — Réservez au 438-600-4440",
      metaTitle: "Taxi Aéroport Montréal-Trudeau YUL | MTL Taxi 438-600-4440",
      metaDesc: "Taxi navette aéroport Montréal-Trudeau (YUL) disponible 24h/24. Tarif fixe centre-ville, réservation à l'avance, minivan pour groupes. Appelez MTL Taxi au 438-600-4440.",
      intro: "MTL Taxi assure les transferts entre l'aéroport international Montréal-Trudeau (YUL) et tous les quartiers de Montréal et du Grand Montréal. Que vous arriviez tard la nuit ou partiez tôt le matin, notre service est disponible 24 heures sur 24, 7 jours sur 7, 365 jours par an. Réservez à l'avance pour garantir votre prise en charge sans attente.",
      ratesTitle: "Tarif taxi aéroport Montréal",
      ratesIntro: "Le tarif entre le centre-ville de Montréal et l'aéroport Trudeau est fixé par la Commission des transports du Québec :",
      ratesNote: "Pour les adresses hors zone centre-ville, le taximètre s'applique. Consultez notre page",
      ratesLink: "Tarifs",
      ratesLinkSuffix: "pour le détail complet.",
      whyTitle: "Pourquoi réserver votre taxi aéroport avec MTL Taxi ?",
      whyItems: [
        "Prise en charge à l'heure — nous suivons votre vol",
        "Pas d'attente au comptoir : votre chauffeur vous attend",
        "Véhicules climatisés, propres et inspectés",
        "Minivan disponible pour groupes jusqu'à 6 passagers",
        "Paiement par carte de crédit, débit ou comptant",
        "Tarif fixe confirmé à la réservation — aucune surprise"
      ],
      howTitle: "Comment réserver votre navette aéroport ?",
      step1: "Appelez le 438-600-4440 ou réservez en ligne",
      step2: "Indiquez votre adresse, heure de vol et nombre de passagers",
      step3: "Votre chauffeur vous attend à l'heure convenue",
      groupsTitle: "Taxi aéroport pour groupes et familles",
      groupsText: "Vous voyagez en famille ou entre collègues ? Notre minivan 6 passagers est idéal pour rejoindre l'aéroport Trudeau avec bagages et confort. Un seul véhicule, un seul tarif — plus économique que plusieurs taxis séparés.",
      groupsLink: "En savoir plus sur notre minivan",
      businessTitle: "Taxi aéroport pour voyageurs d'affaires",
      businessText: "MTL Taxi propose des comptes corporatifs avec facturation mensuelle centralisée pour les entreprises dont les employés voyagent régulièrement. Reçus détaillés fournis pour chaque course.",
      businessLink: "En savoir plus sur les comptes corporatifs",
      ctaTitle: "Réservez votre taxi aéroport maintenant",
      ctaText: "Disponible 24h/24 — appelez le 438-600-4440 ou réservez via notre partenaire en ligne.",
      callBtn: "Appeler 438-600-4440",
      bookBtn: "Réserver en ligne",
      footerHome: "← Accueil",
      footerRates: "Tarifs",
      footerMinivan: "Minivan",
      footerCorporate: "Corporatif",
      tableHeaders: {
        route: "Trajet",
        dayRate: "Tarif de jour (5h–23h)",
        nightRate: "Tarif de nuit (23h–5h)"
      },
      tableRows: [
        { route: "Centre-ville ↔ Aéroport YUL", day: "49,45 $", night: "56,70 $" },
        { route: "Hors centre-ville", day: "Compteur", night: "Compteur" },
        { route: "Minivan groupe (6 passagers)", day: "Sur demande", night: "Sur demande" }
      ]
    },
    en: {
      title: "Montreal-Trudeau Airport Taxi (YUL) — Book at 438-600-4440",
      metaTitle: "Montreal-Trudeau Airport YUL Taxi | MTL Taxi 438-600-4440",
      metaDesc: "Montreal-Trudeau Airport (YUL) taxi shuttle available 24/7. Fixed downtown rate, advance booking, minivan for groups. Call MTL Taxi at 438-600-4440.",
      intro: "MTL Taxi provides transfers between Montreal-Trudeau International Airport (YUL) and all neighborhoods in Montreal and Greater Montreal. Whether you arrive late at night or leave early in the morning, our service is available 24 hours a day, 7 days a week, 365 days a year. Book in advance to guarantee your pickup without waiting.",
      ratesTitle: "Montreal Airport Taxi Rates",
      ratesIntro: "The rate between downtown Montreal and Trudeau Airport is set by the Commission des transports du Québec:",
      ratesNote: "For addresses outside the downtown zone, the meter applies. See our",
      ratesLink: "Rates",
      ratesLinkSuffix: "page for full details.",
      whyTitle: "Why book your airport taxi with MTL Taxi?",
      whyItems: [
        "On-time pickup — we track your flight",
        "No waiting at the counter: your driver awaits you",
        "Air-conditioned, clean and inspected vehicles",
        "Minivan available for groups up to 6 passengers",
        "Payment by credit card, debit or cash",
        "Fixed rate confirmed at booking — no surprises"
      ],
      howTitle: "How to book your airport shuttle?",
      step1: "Call 438-600-4440 or book online",
      step2: "Provide your address, flight time and number of passengers",
      step3: "Your driver awaits you at the agreed time",
      groupsTitle: "Airport taxi for groups and families",
      groupsText: "Traveling with family or colleagues? Our 6-passenger minivan is ideal for getting to Trudeau Airport with luggage and comfort. One vehicle, one rate — more economical than multiple separate taxis.",
      groupsLink: "Learn more about our minivan",
      businessTitle: "Airport taxi for business travelers",
      businessText: "MTL Taxi offers corporate accounts with centralized monthly billing for companies whose employees travel regularly. Detailed receipts provided for each trip.",
      businessLink: "Learn more about corporate accounts",
      ctaTitle: "Book your airport taxi now",
      ctaText: "Available 24/7 — call 438-600-4440 or book through our online partner.",
      callBtn: "Call 438-600-4440",
      bookBtn: "Book online",
      footerHome: "← Home",
      footerRates: "Rates",
      footerMinivan: "Minivan",
      footerCorporate: "Corporate",
      tableHeaders: {
        route: "Route",
        dayRate: "Day Rate (5am–11pm)",
        nightRate: "Night Rate (11pm–5am)"
      },
      tableRows: [
        { route: "Downtown ↔ YUL Airport", day: "$49.45", night: "$56.70" },
        { route: "Outside downtown", day: "Meter", night: "Meter" },
        { route: "Minivan group (6 passengers)", day: "On request", night: "On request" }
      ]
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen pt-20" data-testid="airport-page">
      <SEO 
        title={t.metaTitle}
        description={t.metaDesc}
        canonical={`https://mtl.taxi/${language === 'fr' ? 'fr/aeroport-yul' : 'en/yul-airport'}`}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Plane className="w-4 h-4" />
            YUL - Pierre-Elliott-Trudeau
          </div>
          <h1 
            className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight leading-tight"
            data-testid="airport-title"
          >
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </div>
      </section>

      {/* Rates Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.ratesTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {t.ratesIntro}
          </p>

          <Card className="border-0 shadow-lg overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary text-white">
                  <TableHead className="font-bold text-white">{t.tableHeaders.route}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.tableHeaders.dayRate}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.tableHeaders.nightRate}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.tableRows.map((row, index) => (
                  <TableRow key={index} className="hover:bg-muted/20">
                    <TableCell className="font-medium">{row.route}</TableCell>
                    <TableCell className="text-center font-bold text-primary">{row.day}</TableCell>
                    <TableCell className="text-center font-bold text-primary">{row.night}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="p-4 bg-blue-50 border-l-4 border-primary rounded-r-lg">
            <p className="text-foreground">
              {t.ratesNote} <Link to={getRoute('/fares')} className="text-primary font-semibold hover:underline">{t.ratesLink}</Link> {t.ratesLinkSuffix}
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-8">
            {t.whyTitle}
          </h2>
          <ul className="space-y-4">
            {t.whyItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-foreground text-lg">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How to Book Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-10">
            {t.howTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: 1, icon: Phone, text: t.step1 },
              { num: 2, icon: Users, text: t.step2 },
              { num: 3, icon: Clock, text: t.step3 }
            ].map((step, index) => (
              <Card key={index} className="border-0 shadow-md text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {step.num}
                  </div>
                  <p className="font-medium text-foreground">{step.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Groups Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.groupsTitle}
          </h2>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-muted-foreground text-lg mb-4">{t.groupsText}</p>
              <Link to={getRoute('/services')} className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                {t.groupsLink} →
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.businessTitle}
          </h2>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-muted-foreground text-lg mb-4">{t.businessText}</p>
              <Link to={getRoute('/services')} className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                {t.businessLink} →
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-accent-foreground/80 text-lg mb-8">
            {t.ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t.bookBtn}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-8 bg-muted/50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to={getRoute('/')} className="text-muted-foreground hover:text-primary">{t.footerHome}</Link>
            <Link to={getRoute('/fares')} className="text-muted-foreground hover:text-primary">{t.footerRates}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerMinivan}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerCorporate}</Link>
          </nav>
        </div>
      </section>
    </div>
  );
};

export default AirportPage;
